/** @graphql cc **/

import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { useContext, useEffect, useState } from 'react';

import { BULK_SPACE_PERMISSIONS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useBulkRoleFlags } from './useBulkRoleFlags.tsx';
import type { useViewPermissionsModalPermissionsQuery as useViewPermissionsModalPermissionsQueryType } from './__types__/useViewPermissionsModalPermissionsQuery.ts';
import type { useViewPermissionsModalRolesQuery as useViewPermissionsModalRolesQueryType } from './__types__/useViewPermissionsModalRolesQuery.ts';

export type SpacePermissionNodes = NonNullable<
	useViewPermissionsModalPermissionsQueryType['spacePermissionsAll']
>['nodes'];

export type RoleNodes = NonNullable<
	useViewPermissionsModalRolesQueryType['spaceRolesByCriteria']
>['nodes'];

interface SpacePermissions {
	loading: boolean;
	permissions: SpacePermissionNodes;
	roles: RoleNodes;
}

const useViewPermissionsModalPermissionsQuery = gql`
	query useViewPermissionsModalPermissionsQuery {
		spacePermissionsAll {
			nodes {
				id
				displayName
				description
				hubDisplayName
				hubDescription
				priority
				group {
					displayName
					priority
				}
				requiredSpacePermissions
			}
		}
	}
`;
const useViewPermissionsModalRolesQuery = gql`
	query useViewPermissionsModalRolesQuery {
		spaceRolesByCriteria {
			nodes {
				roleId
				roleDisplayName
				roleDescription
				roleType
				spacePermissionList {
					id
					priority
					displayName
					description
					hubDisplayName
					hubDescription
					requiredSpacePermissions
				}
			}
			pageInfo {
				endCursor
				hasNextPage
			}
		}
	}
`;

export const useViewPermissionsModal = ({
	flags,
}: {
	flags: FlagsStateContainer;
}): SpacePermissions => {
	const [experienceStarted, setExperienceStarted] = useState(false);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { showErrorFlag } = useBulkRoleFlags({ flags });

	const fail = (error: Error) => {
		const experienceFailure = error || new Error('An error occurred');

		experienceTracker.fail({
			name: BULK_SPACE_PERMISSIONS_EXPERIENCE,
			error: experienceFailure,
		});

		void showErrorFlag();
	};

	const { data: permissionsData, loading } = useQuery<useViewPermissionsModalPermissionsQueryType>(
		useViewPermissionsModalPermissionsQuery,
		{
			onCompleted: () => {
				setExperienceStarted(false);
				experienceTracker.succeed({
					name: BULK_SPACE_PERMISSIONS_EXPERIENCE,
				});
			},
			onError: (error) => {
				setExperienceStarted(false);
				fail(error);
			},
		},
	);

	const { data: rolesData, loading: rolesLoading } =
		useQuery<useViewPermissionsModalRolesQueryType>(useViewPermissionsModalRolesQuery, {
			onCompleted: () => {
				setExperienceStarted(false);
				experienceTracker.succeed({
					name: BULK_SPACE_PERMISSIONS_EXPERIENCE,
				});
			},
			onError: (error) => {
				setExperienceStarted(false);
				fail(error);
			},
		});

	useEffect(() => {
		if (loading || (rolesLoading && !experienceStarted)) {
			experienceTracker.start({
				name: BULK_SPACE_PERMISSIONS_EXPERIENCE,
			});
		}
	}, [loading, rolesLoading, experienceStarted, experienceTracker]);

	return {
		loading,
		permissions: permissionsData?.spacePermissionsAll?.nodes || [],
		roles: rolesData?.spaceRolesByCriteria?.nodes || [],
	};
};
